import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { MaterialIcon } from '@dsny/dsny-component-library'
import { AIFilled } from '@dsny/dsny-component-library/icons'
import { thunks as playQueueThunks } from 'reducers/PlayQueue'
import { PLAY_SOURCES, PLAY_TRIGGERS } from 'constants/tracking'
import { formatSeconds } from 'utils/stringHelpers'
import TrackActionsDropdown from 'components/Dropdowns/TrackActionsDropdown/TrackActionsDropdown'
import LottieAnimation from 'components/LottieAnimation/LottieAnimation'
import MetaData from '../MetaData/MetaData'
import {
  TrackContainer,
  TrackDuration,
  TrackTitle,
  IconsWrapper,
  TrackNumber,
  TrackLeadingCell,
  TrackRowBody,
  TrackContentCluster,
  PlayIcon,
  PlayWrapper,
  ArtistName,
  SecondRow,
  TrackVersion,
  TitleRow,
  Wrapper,
  TrackMetadataExpandButton
} from './Track.styles.js'
import useReleaseInformationResponsiveBreakpointsV2 from '../../hooks/useReleaseInformationResponsiveBreakpointsV2'

export default function TrackListRow({
  track = {},
  index = 0,
  isCanadianContent = false,
  showMetadata = false,
  isExpanded = false
}) {
  const theme = useTheme()
  const responsiveBreakpointsV2 = useReleaseInformationResponsiveBreakpointsV2()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { isPlaying, song } = useSelector((state) => state.player)
  const isCurrentSongPlaying = isPlaying && track?.trackid === song?.trackid
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(isExpanded)

  const playSong = () => {
    dispatch(
      playQueueThunks.playSong(track, {
        source: PLAY_SOURCES.RELEASE_INFORMATION,
        trigger: PLAY_TRIGGERS.ROW
      })
    )
  }

  const toggleMetadata = (event) => {
    event.stopPropagation()
    setIsMetadataExpanded((currentValue) => !currentValue)
  }

  useEffect(() => {
    setIsMetadataExpanded(Boolean(isExpanded))
  }, [isExpanded])

  const hasRowIcons =
    track?.explicit || track?.is_ai_generated || isCanadianContent

  return (
    <TrackContainer>
      <TrackRowBody
        $responsiveBreakpointsV2={responsiveBreakpointsV2}
        onClick={playSong}
      >
        <TrackContentCluster $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          <TrackLeadingCell>
            {isCurrentSongPlaying ? (
              <LottieAnimation />
            ) : (
              <>
                <TrackNumber>{index + 1}</TrackNumber>
                <PlayWrapper>
                  <PlayIcon name="PlayArrow" color={theme.colors.neutralW100} />
                </PlayWrapper>
              </>
            )}
          </TrackLeadingCell>
          <Wrapper>
            <TitleRow>
              <TrackTitle
                $isPlaying={isCurrentSongPlaying}
                className="truncate-text"
              >
                {track?.title}
              </TrackTitle>
              <TrackVersion $isPlaying={isCurrentSongPlaying}>
                {track?.version && `(${track?.version})`}
              </TrackVersion>
            </TitleRow>

            <SecondRow>
              {hasRowIcons && (
                <IconsWrapper>
                  {track?.explicit && (
                    <MaterialIcon
                      name="Explicit"
                      color={theme.colors.neutralW100}
                    />
                  )}

                  {track?.is_ai_generated && (
                    <AIFilled color={theme.colors.neutralW100} />
                  )}

                  {isCanadianContent && <MaterialIcon name="CanadaFlag" />}
                </IconsWrapper>
              )}
              <ArtistName className="truncate-text">{track?.artist}</ArtistName>
            </SecondRow>
          </Wrapper>
        </TrackContentCluster>
        <TrackDuration $responsiveBreakpointsV2={responsiveBreakpointsV2}>
          {formatSeconds(track?.duration)}
        </TrackDuration>
        <TrackActionsDropdown track={track} />
        {showMetadata && (
          <TrackMetadataExpandButton
            aria-label={
              isMetadataExpanded
                ? t('release-info.collapse-track-details', {
                    title: track?.title || t('release-info.track')
                  })
                : t('release-info.expand-track-details', {
                    title: track?.title || t('release-info.track')
                  })
            }
            type="button"
            onClick={toggleMetadata}
          >
            <MaterialIcon
              name={isMetadataExpanded ? 'ExpandLess' : 'ExpandMore'}
              color={theme.colors.neutralW100}
            />
          </TrackMetadataExpandButton>
        )}
      </TrackRowBody>
      {showMetadata && isMetadataExpanded && <MetaData track={track} />}
    </TrackContainer>
  )
}

TrackListRow.propTypes = {
  track: PropTypes.shape({
    trackid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    artist: PropTypes.string,
    version: PropTypes.string,
    duration: PropTypes.number,
    explicit: PropTypes.bool,
    is_ai_generated: PropTypes.bool
  }),
  index: PropTypes.number,
  isCanadianContent: PropTypes.bool,
  showMetadata: PropTypes.bool,
  isExpanded: PropTypes.bool
}
